/**
 * API에서 전달된 중첩된(nested) 레이아웃 구조(SECTION, ROW 등)를
 * DocumentFormEditArea 프론트엔드 포맷(1차원 배열)으로 변환(Flatten)합니다.
 */

/**
 * ROW 안의 항목 개수(N)를 세서 12 / N 만큼의 Bootstrap 컬럼 클래스를 반환.
 * 예: 1개 -> col-12, 2개 -> col-12 col-md-6, 3개 -> col-12 col-md-4
 */
function getResponsiveColClass(itemCount) {
    if (!itemCount || itemCount <= 1) return 'col-12';

    // 12 그리드 시스템 내에서 안전하게 분할 가능하도록 계산
    const mdSpan = Math.max(12 / itemCount, 2); // 최대 6개(col-md-2)까지만 분할, 그 이상은 너무 좁아지므로 방어
    const roundedSpan = Math.floor(mdSpan);
    return `col-12 col-md-${roundedSpan}`;
}

/**
 * Convert snake_case or space-separated strings to camelCase
 */
export function toCamelCase(str) {
    if (!str || (str.indexOf('_') === -1 && str.indexOf(' ') === -1)) return str;
    return str.replace(/[_ ]+(.)/g, (match, p1) => p1.toUpperCase());
}

/**
 * 오브젝트 인스턴스/DB JSON 키와 레이아웃 필드 key 매칭용 (공백·하이픈·camel/snake 혼재)
 * 예: "school name" · schoolName · school_name → 동일 "school_name" 으로 비교
 */
export function toComparableDataKey(str) {
    if (str == null || str === '') return '';
    let s = String(str).trim();
    if (!s) return '';
    s = s.replace(/([a-z0-9])([A-Z])/g, '$1_$2');
    s = s.toLowerCase();
    s = s.replace(/[\s-]+/g, '_');
    s = s.replace(/_+/g, '_');
    s = s.replace(/^_|_$/g, '');
    return s;
}

/**
 * FMS/API가 ARRAY 행을 snake_case 등으로 줄 때, 레이아웃 컬럼 key( camelCase·공백 정규화 후 )와 맞춰
 * 각 행 객체를 다시 채웁니다. (저장 키를 한 종류로 맞춘 뒤 조회 시 키 불일치로 빈 칸 나는 현상 완화)
 */
export function normalizeArrayRowsForLayoutFields(arrayVal, columnFields) {
    if (!Array.isArray(arrayVal) || !Array.isArray(columnFields) || columnFields.length === 0) {
        return arrayVal;
    }
    return arrayVal.map((rowObj) => {
        if (!rowObj || typeof rowObj !== 'object') return rowObj;
        const out = {};
        for (const col of columnFields) {
            const ck = col.key;
            if (!ck) continue;
            let v = rowObj[ck];
            if (v === undefined) {
                const target = toComparableDataKey(ck);
                for (const ok of Object.keys(rowObj)) {
                    if (toComparableDataKey(ok) === target) {
                        v = rowObj[ok];
                        break;
                    }
                }
            }
            if (v !== undefined) out[ck] = v;
        }
        return out;
    });
}

/**
 * 단일 객체 필드를 프론트엔드 포맷에 맞춰 정제합니다.
 */
function normalizeField(f, colClass = 'col-12') {
    // 이미 구조화된 경우 보존
    const fieldData = { ...f, col: colClass };

    // FMS layoutConfig: display_type → displayType (스네이크/카멜 혼용)
    if (fieldData.displayType == null && fieldData.display_type != null) {
        fieldData.displayType = fieldData.display_type;
    }
    if (fieldData.inputMode == null && fieldData.input_mode != null) {
        fieldData.inputMode = fieldData.input_mode;
    }

    // EXTERNAL_TABLE 여부 확인
    const isExternal = fieldData.type === 'ExternalTable'
        || fieldData.tableName === 'cetus_user'
        || fieldData.attributeSource === 'EXTERNAL_TABLE';

    if (!isExternal) {
        if (fieldData.key) {
            fieldData.key = toCamelCase(fieldData.key);
        }
        if (fieldData.id) {
            fieldData.id = toCamelCase(fieldData.id);
        }
        if (fieldData.parent_key) {
            if (Array.isArray(fieldData.parent_key)) {
                fieldData.parent_key = fieldData.parent_key.map(pk => {
                    if (pk.endsWith('[]')) {
                        return toCamelCase(pk.slice(0, -2)) + '[]';
                    }
                    return toCamelCase(pk);
                });
            }
        }
    }

    // 프론트의 필수 규칙 통일 (`title`은 섹션/블록명, `label`은 필드명)
    // 백엔드에서 label이나 name 등으로 올 경우 보완
    if (!fieldData.type) {
        const normalizedDataType = String(fieldData.dataType || '').toUpperCase().replace(/[\s-]/g, '_');
        // API에 type 명시가 없으면 dataType을 참조하여 기본 타입 지정
        if (normalizedDataType === 'IMAGE_TYPE' || normalizedDataType === 'IMAGE') {
            fieldData.type = 'IMAGE';
        } else if (normalizedDataType === 'FILE_TYPE' || normalizedDataType === 'FILE') {
            fieldData.type = 'FILE';
        } else if (normalizedDataType === 'BOOLEAN') {
            fieldData.type = 'BOOLEAN';
        } else if (normalizedDataType === 'ARRAY') {
            fieldData.type = 'ARRAY';
        } else if (normalizedDataType === 'ENUM_TYPE' || normalizedDataType === 'ENUM') {
            fieldData.type = 'ENUM_TYPE';
        } else if (normalizedDataType === 'DATE_TYPE' || normalizedDataType === 'DATE' || normalizedDataType === 'DATA_TYPE') {
            fieldData.type = 'DATE_TYPE';
        } else {
            // 그 외에는 텍스트 필드로 취급
            fieldData.type = 'TEXT';
        }
    }

    return fieldData;
}

/**
 * 최상위 ROW·낱개 필드·ARRAY 가 SECTION 으로 감싸지지 않은 layoutConfig 도 flatten 되도록 보정.
 * (flattenLayout 은 SECTION/DIVIDER 만 순회하므로, 최상위 ARRAY/ROW 는 그대로 두면 무시됨)
 */
export function normalizePocApplicationSections(sections) {
    if (!Array.isArray(sections)) return [];
    return sections.map((sec) => {
        if (!sec || typeof sec !== 'object') return sec;
        if (sec.type === 'SECTION' || sec.type === 'DIVIDER') return sec;
        const dt = String(sec.dataType || '').toUpperCase().replace(/[\s-]/g, '_');
        // FMS layoutConfig: JSON 블록 + columns(협약 조항 등) — 자식 필드로 펼침
        if ((dt === 'JSON' || dt === 'JSON_TYPE') && Array.isArray(sec.columns) && sec.columns.length) {
            return {
                type: 'SECTION',
                title: sec.label || sec.title || '',
                fields: sec.columns.map((c) => ({ ...c })),
            };
        }
        const isArrayBlock = sec.dataType === 'ARRAY' || sec.type === 'ARRAY';
        const isRow = sec.type === 'ROW';
        const isBareScalar =
            sec.key &&
            (sec.label != null || sec.dataType != null) &&
            !isArrayBlock &&
            !isRow;
        if (isRow || isArrayBlock) {
            return { type: 'SECTION', title: sec.label || sec.title || '', fields: [sec] };
        }
        // 낱개 필드: SECTION 제목에 label 을 넣으면 미리보기 표에서 구분열·라벨열에 동일 문구가 두 번 나옴 → title 비움
        if (isBareScalar) {
            return { type: 'SECTION', title: '', fields: [sec] };
        }
        return sec;
    });
}

/** 최상위에서 SECTION/DIVIDER가 아닌 낱개 필드( key + label/dataType )인지 여부 */
function isBareField(sec) {
    if (!sec || typeof sec !== 'object') return false;
    if (sec.type === 'SECTION' || sec.type === 'DIVIDER') return false;
    // ROW · ARRAY 는 섹션 추출(extractFields)에서 다루므로 낱개 필드로 수집하면 안 됨
    if (sec.type === 'ROW') return false;
    if (sec.type === 'ARRAY' || sec.dataType === 'ARRAY') return false;
    return Boolean(sec.key && (sec.label != null || sec.dataType != null));
}

export function flattenLayout(apiSections) {
    const flatLayout = [];
    const bareFieldsCollector = [];
    const sectionsNormalized = normalizePocApplicationSections(
        Array.isArray(apiSections) ? apiSections : []
    );

    // 재귀 탐색 함수
    const traverse = (sections, parentTitle = '') => {
        if (!Array.isArray(sections)) return;

        sections.forEach((sec) => {
            // 0. 낱개 필드(동의 항목 등)는 수집 후 마지막에 한 섹션으로 추가
            if (isBareField(sec)) {
                bareFieldsCollector.push(normalizeField(sec));
                return;
            }
            // 1. SECTION 안에 하위 SECTION이 섞여 있으면(ROW·ARRAY와 병렬) 그룹 순서 유지:
            //    비-SECTION 들을 부모 타이틀의 한 블록으로 먼저 평탄화한 뒤, 하위 SECTION 각각 처리.
            //    기존에는 traverse(fields)만 해서 ROW·ARRAY가 unhandled 로 누락되고,
            //    ARRAY는 isBareField 에 걸려 BARE 로 잘못 수집되는 문제가 있었음.
            if (sec.type === 'SECTION' && sec.fields && sec.fields.some((f) => f && f.type === 'SECTION')) {
                const currentTitle =
                    sec.title != null && String(sec.title).trim() !== '' ? sec.title : parentTitle;
                const buffer = [];
                const flushBuffer = () => {
                    if (buffer.length === 0) return;
                    traverse(
                        [{ type: 'SECTION', title: currentTitle, fields: [...buffer] }],
                        parentTitle
                    );
                    buffer.length = 0;
                };
                sec.fields.forEach((f) => {
                    if (f && f.type === 'SECTION') {
                        flushBuffer();
                        traverse([f], currentTitle);
                    } else {
                        buffer.push(f);
                    }
                });
                flushBuffer();
                return;
            }
            // 2. 현재 SECTION이 제일 깊은 말단 섹션이라면, 평탄화하여 1차원 목록에 추가
            else if (sec.type === 'SECTION' || sec.type === 'DIVIDER') {
                const sectionItems = sec.fields || sec.items || [];

                const extractFields = (itemsObj, defaultCol = 'col-12', forceMobileCol = false) => {
                    const resultFields = [];
                    itemsObj.forEach((fieldOrRow) => {
                        if (fieldOrRow.type === 'ROW') {
                            const rowFields = fieldOrRow.fields || [];

                            // 가로 분할 그룹(ROW)은 자식 갯수(N) 단위를 가짐.
                            const childrenColClass = getResponsiveColClass(rowFields.length);

                            const nestedFields = extractFields(rowFields, childrenColClass, forceMobileCol);
                            resultFields.push({
                                type: 'ROW',
                                col: defaultCol,
                                fields: nestedFields
                            });
                        } else if (fieldOrRow.type === 'SECTION') {
                            // 수직 그룹(SECTION)
                            const nestedFields = extractFields(fieldOrRow.fields || [], 'col-12', forceMobileCol);
                            resultFields.push({
                                type: 'SECTION',
                                title: fieldOrRow.title,
                                col: defaultCol,
                                fields: nestedFields
                            });
                        } else if (String(fieldOrRow.dataType || '').toUpperCase().replace(/[\s-]/g, '_') === 'JSON') {
                            // 중첩 JSON+columns: 테이블(ARRAY)이 아니라 필드 나열로 펼침
                            const jsonFields = fieldOrRow.fields || fieldOrRow.columns || [];
                            extractFields(jsonFields, 'col-12', forceMobileCol).forEach((nf) => {
                                resultFields.push(nf);
                            });
                        } else if (fieldOrRow.dataType === 'ARRAY' || fieldOrRow.type === 'ARRAY') {
                            const arrayFields = fieldOrRow.fields || fieldOrRow.columns || [];
                            const nestedFields = extractFields(arrayFields, 'col-12', forceMobileCol);

                            const arrField = normalizeField(fieldOrRow, defaultCol);
                            arrField.type = 'ARRAY';
                            arrField.fields = nestedFields;

                            resultFields.push(arrField);
                        } else if (fieldOrRow.type === 'GROUP' && Array.isArray(fieldOrRow.fields)) {
                            // GROUP: 기간(시작일 ~ 종료일) 등 한 레이블 아래 여러 필드를 구분자로 이어서 표시
                            const groupFields = (fieldOrRow.fields || []).map((f) => normalizeField(f, 'col-12'));
                            resultFields.push({
                                type: 'GROUP',
                                title: fieldOrRow.title || '',
                                separator: fieldOrRow.separator ?? ' ~ ',
                                fields: groupFields,
                                col: defaultCol,
                            });
                        } else {
                            // 단일 텍스트/이미지 등 필드인 경우, 부모가 계산해둔 colClass(defaultCol) 적용
                            resultFields.push(normalizeField(fieldOrRow, defaultCol));
                        }
                    });
                    return resultFields;
                };

                const flatFields = extractFields(sectionItems);

                // 1차원 평면 배열에 완성된 섹션 푸시
                if (sec.type === 'DIVIDER') {
                    flatLayout.push({
                        title: sec.title || '',
                        type: 'DIVIDER',
                        items: flatFields,
                    });
                } else {
                    flatLayout.push({
                        title: sec.title ?? parentTitle ?? '섹션',
                        type: 'SECTION',
                        fields: flatFields,
                    });
                }
            }
            else {
                // 최상위에서 SECTION이 아닌 낱개 ROW나 필드가 떨어지는 예외 케이스 처리
                console.warn('Unhandled top-level element type:', sec.type);
            }
        });
    };

    traverse(sectionsNormalized);

    // 섹션에 들어가지 않은 낱개 필드는 섹션 없이 입력만 나오도록 각각 BARE 블록으로 추가
    bareFieldsCollector.forEach((field) => {
        flatLayout.push({
            type: 'BARE',
            title: '',
            fields: [field],
        });
    });

    return { layout: flatLayout };
}

/** flatten + normalizeField 이후 구조에서 FILE 필드 판별 */
function isLayoutFileField(node) {
    if (!node || typeof node !== 'object') return false;
    const dt = String(node.dataType || '').toUpperCase().replace(/[\s-]/g, '_');
    return node.type === 'FILE' || dt === 'FILE_TYPE' || dt === 'FILE';
}

function walkLayoutFieldTree(nodes, visitField) {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
        if (!node || typeof node !== 'object') continue;
        if (node.type === 'ROW' && Array.isArray(node.fields)) {
            walkLayoutFieldTree(node.fields, visitField);
            continue;
        }
        if (node.type === 'SECTION' && Array.isArray(node.fields)) {
            walkLayoutFieldTree(node.fields, visitField);
            continue;
        }
        if (node.type === 'GROUP' && Array.isArray(node.fields)) {
            walkLayoutFieldTree(node.fields, visitField);
            continue;
        }
        if (node.type === 'ARRAY' || node.dataType === 'ARRAY') {
            if (Array.isArray(node.fields)) walkLayoutFieldTree(node.fields, visitField);
            continue;
        }
        if (isLayoutFileField(node)) {
            visitField(node);
        }
    }
}

function forEachFileFieldInStructure(structureJson, visitor) {
    const layout = structureJson?.layout;
    if (!Array.isArray(layout)) return;
    for (const block of layout) {
        const nodes = block?.type === 'DIVIDER' ? block.items : block.fields;
        walkLayoutFieldTree(nodes, visitor);
    }
}

/**
 * layoutConfig를 flatten 한 structureJson( useDocumentFormState.structureJson )에서
 * FILE_TYPE 필드의 key 목록을 수집한다. (키는 normalizeField 에 의해 camelCase 일 수 있음)
 */
export function collectFileFieldKeysFromLayoutStructure(structureJson) {
    const keys = [];
    const seen = new Set();
    forEachFileFieldInStructure(structureJson, (field) => {
        const k = field.key;
        if (!k || seen.has(k)) return;
        seen.add(k);
        keys.push(k);
    });
    return keys;
}

function parseMaxFileCountFromValidationRule(rawRule) {
    if (rawRule == null) return null;
    try {
        const rule = typeof rawRule === 'string' ? JSON.parse(rawRule) : rawRule;
        if (!rule || typeof rule !== 'object') return null;
        if (typeof rule.maxFileCount === 'number' && rule.maxFileCount > 0) return rule.maxFileCount;
        if (typeof rule.max_file_count === 'number' && rule.max_file_count > 0) return rule.max_file_count;
    } catch (e) {
        return null;
    }
    return null;
}

function attributeUidFromLayoutField(field) {
    const u = field?.objectAttributeUid ?? field?.object_attribute_uid;
    if (u == null || u === '') return null;
    const n = Number(u);
    return Number.isFinite(n) ? n : null;
}

function findAttributeByUid(pocAttributes, uid) {
    if (!Array.isArray(pocAttributes) || uid == null) return null;
    return (
        pocAttributes.find((a) => {
            if (!a || typeof a !== 'object') return false;
            const aid = a.uid ?? a.attributeUid ?? a.attribute_uid;
            if (aid == null || aid === '') return false;
            return Number(aid) === Number(uid);
        }) || null
    );
}

/**
 * 레이아웃 FILE 필드의 objectAttributeUid → pocAttributes 매칭 후 validationRule.maxFileCount
 */
export function resolveMaxFileCountForLayoutFileFields(structureJson, pocAttributes) {
    try {
        if (!Array.isArray(pocAttributes) || pocAttributes.length === 0) return 1;
        let found = null;
        forEachFileFieldInStructure(structureJson, (field) => {
            if (found != null) return;
            const uid = attributeUidFromLayoutField(field);
            if (uid == null) return;
            const attr = findAttributeByUid(pocAttributes, uid);
            if (!attr) return;
            const rawRule = attr.validationRule ?? attr.validation_rule;
            const n = parseMaxFileCountFromValidationRule(rawRule);
            if (n != null) found = n;
        });
        return found != null ? found : 1;
    } catch (e) {
        return 1;
    }
}

/** layoutConfig sections 에서 지정 key 필드를 제거 (ROW 내부 포함) */
export function omitLayoutStructureFieldKeys(structure, keysToOmit = []) {
    if (!structure || !Array.isArray(structure.sections) || keysToOmit.length === 0) {
        return structure;
    }
    const omit = new Set(keysToOmit.map((k) => String(k).toLowerCase()));
    const shouldOmit = (key) => key != null && omit.has(String(key).toLowerCase());

    return {
        ...structure,
        sections: structure.sections.map((section) => {
            if (!section || !Array.isArray(section.fields)) return section;
            const fields = section.fields
                .map((fieldOrRow) => {
                    if (fieldOrRow?.type === 'ROW' && Array.isArray(fieldOrRow.fields)) {
                        const rowFields = fieldOrRow.fields.filter((f) => !shouldOmit(f?.key));
                        if (rowFields.length === 0) return null;
                        return { ...fieldOrRow, fields: rowFields };
                    }
                    if (shouldOmit(fieldOrRow?.key)) return null;
                    return fieldOrRow;
                })
                .filter(Boolean);
            return { ...section, fields };
        }),
    };
}

const formAdapter = {
    flattenLayout,
    toComparableDataKey,
    normalizePocApplicationSections,
    collectFileFieldKeysFromLayoutStructure,
    resolveMaxFileCountForLayoutFileFields,
    omitLayoutStructureFieldKeys,
};
export default formAdapter;
